import i18n from '../../../i18n';
import type { ConnectedSourceId } from '../types';

export type DataSource = {
  id: ConnectedSourceId;
  name: string;
  kind: string;
  desc: string;
  core?: boolean;
  /**
   * First-class source: on whenever the server offers it, never toggleable.
   * Missing data opens a connect explainer instead of an opt-in toggle.
   */
  alwaysOn?: boolean;
  /** Offered by the server when configured, but hidden until the user connects it. */
  optOutByDefault?: boolean;
  unlocks: string[];
};

function sourceEntry(
  id: ConnectedSourceId,
  extras: Omit<DataSource, 'id' | 'name' | 'kind' | 'desc'>,
): DataSource {
  return {
    id,
    get name() {
      return i18n.t(`chrome:sources.${id}.name`);
    },
    get kind() {
      return i18n.t(`chrome:sources.${id}.kind`);
    },
    get desc() {
      return i18n.t(`chrome:sources.${id}.desc`);
    },
    ...extras,
  };
}

export const SOURCES: DataSource[] = [
  sourceEntry('reservations', {
    core: true,
    unlocks: ['occupancy', 'covers', 'incoming', 'seatTime', 'efficiency', 'noshow'],
  }),
  sourceEntry('tableplan', {
    core: true,
    unlocks: ['efficiency', 'seatTime'],
  }),
  sourceEntry('alenopay', {
    unlocks: ['sellitems'],
  }),
  sourceEntry('pos', {
    alwaysOn: true,
    unlocks: ['revenue', 'spend', 'avgCheck', 'revpash'],
  }),
  sourceEntry('ga', {
    unlocks: ['web', 'marketing'],
  }),
  sourceEntry('googleads', {
    unlocks: ['marketing', 'ads'],
  }),
  sourceEntry('gsc', {
    unlocks: ['marketing', 'seo'],
  }),
  sourceEntry('gbp', {
    unlocks: ['marketing', 'local'],
  }),
  sourceEntry('reviews', {
    unlocks: ['sentiment'],
  }),
  sourceEntry('crm', {
    unlocks: ['guests'],
  }),
  sourceEntry('weather', {
    optOutByDefault: true,
    unlocks: ['weather'],
  }),
  sourceEntry('events', {
    optOutByDefault: true,
    unlocks: ['events'],
  }),
  sourceEntry('demandgen', {
    optOutByDefault: true,
    unlocks: ['demandBridge', 'demandCapacity', 'reviewOps'],
  }),
  sourceEntry('opentable', {
    optOutByDefault: true,
    unlocks: [],
  }),
];

export const OPT_OUT_BY_DEFAULT_SOURCES: ConnectedSourceId[] = SOURCES.filter(
  s => s.optOutByDefault,
).map(s => s.id);

/** Sources that stay on whenever the server offers them (no user opt-out). */
export const ALWAYS_ON_SOURCES: ConnectedSourceId[] = SOURCES.filter(
  s => s.alwaysOn || s.core,
).map(s => s.id);

export function sourceHasMetric(
  connected: ConnectedSourceId[],
  metricKey: string,
): boolean {
  return connected.some(c =>
    (SOURCES.find(s => s.id === c)?.unlocks ?? []).includes(metricKey),
  );
}
